/**
 * Authentication hook for managing admin auth state
 */

import { useState, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';
import type { AdminUser } from '../types';

interface UseAuthReturn {
  user: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    const response = await authApi.getMe();
    if (response.success && response.data) {
      setUser(response.data);
    } else {
      localStorage.removeItem('admin_token');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username: string, password: string) => {
    const response = await authApi.login({ username, password });
    
    if (response.success && response.data) {
      setUser({
        id: '',
        username,
        isAdmin: true,
      });
      return { success: true };
    }
    
    return {
      success: false,
      error: response.error?.message || 'Login failed',
    };
  };

  const logout = async () => {
    await authApi.logout();
    setUser(null);
  };

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    checkAuth,
  };
}

export default useAuth;
